import React, { useEffect, useMemo, useState } from "react";
import { Chess } from "chess.js";
import { Chessboard } from "react-chessboard";

/**
 * Chess board — works:
 *  - Standalone (hot-seat, no ws/state)
 *  - In-channel (ws + state). Streamer or any seated player can move; chess.js validates.
 */
export default function ChessGame({ state, sendState }) {
  const shared = !!sendState;
  const [localGame, setLocalGame] = useState(() => new Chess());
  const fen = shared ? (state?.fen || new Chess().fen()) : localGame.fen();
  const game = useMemo(() => {
    const g = new Chess();
    try { g.load(fen); } catch { /* invalid */ }
    return g;
  }, [fen]);

  const [status, setStatus] = useState("");
  useEffect(() => {
    if (game.isCheckmate()) setStatus(`Checkmate — ${game.turn() === "w" ? "Black" : "White"} wins`);
    else if (game.isDraw()) setStatus("Draw");
    else if (game.isCheck()) setStatus(`${game.turn() === "w" ? "White" : "Black"} is in check`);
    else setStatus(`${game.turn() === "w" ? "White" : "Black"} to move`);
  }, [fen, game]);

  const onPieceDrop = ({ sourceSquare, targetSquare }) => {
    if (!targetSquare) return false;
    const g = new Chess(fen);
    try {
      const move = g.move({ from: sourceSquare, to: targetSquare, promotion: "q" });
      if (!move) return false;
      const newFen = g.fen();
      if (shared) sendState({ fen: newFen });
      else setLocalGame(g);
      return true;
    } catch {
      return false;
    }
  };

  const reset = () => {
    const fresh = new Chess();
    if (shared) sendState({ fen: fresh.fen() });
    else setLocalGame(fresh);
  };

  return (
    <div data-testid="game-chess" className="bg-black/60 border border-white/10 rounded-md p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="sc-heading text-xl font-bold">Chess</div>
        <div className="text-xs sc-label text-[#00E5FF]">{status}</div>
      </div>
      <div className="max-w-md mx-auto">
        <Chessboard
          options={{
            position: fen,
            onPieceDrop,
            boardOrientation: "white",
            darkSquareStyle: { backgroundColor: "#27272a" },
            lightSquareStyle: { backgroundColor: "#3f3f46" },
          }}
        />
      </div>
      <div className="mt-4 text-center">
        <button data-testid="chess-reset" onClick={reset} className="sc-btn-ghost text-xs">Reset Board</button>
      </div>
      <p className="text-[10px] text-zinc-500 mt-3 text-center">Rules enforced by chess.js. Drag any piece to legal squares.</p>
    </div>
  );
}
